import React, { useState, useEffect, useRef, useContext } from 'react';
import { io } from 'socket.io-client';
import { api } from '../api';
import { AuthContext } from '../App';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083';

// 8-bit pixel emotes
const EMOTES = {
  ':hype:': '>>!', ':gg:': 'GG', ':love:': '<3', ':lol:': 'xD',
  ':pog:': ':O', ':sadge:': ':{', ':wave:': 'o/', ':clap:': '*clap*',
  ':eyes:': 'o.o', ':skull:': 'x_x', ':goat:': 'GOAT', ':cap:': '[CAP]',
  ':W:': '[W]', ':L:': '[L]', ':crown:': 'K', ':fire:': '~*~',
  ':heart:': '<3', ':star:': '*', ':rage:': '>:(', ':chill:': 'B)',
  ':pray:': '-人-', ':100:': '100!', ':ghost:': '~O~', ':party:': '***',
};

const BADGE_ICONS = {
  streamer: { label: 'STR', color: '#eb0400', title: 'Streamer' },
  mod:      { label: 'MOD', color: '#00c400', title: 'Moderator' },
  sub:      { label: 'SUB', color: '#D6E9AA', title: 'Subscriber' },
};

function renderMessage(text) {
  const parts = text.split(/(:[a-zA-Z0-9_]+:)/g);
  return parts.map((part, i) => {
    if (EMOTES[part]) {
      return <span key={i} className="chat-emote" title={part}>{EMOTES[part]}</span>;
    }
    return part;
  });
}

export default function Chat({ channel }) {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [chatError, setChatError] = useState('');
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [showEmotes, setShowEmotes] = useState(false);
  const [chatSettings, setChatSettings] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const cooldownRef = useRef(null);

  const isStreamer = user?.username === channel;

  // Load chat settings
  useEffect(() => {
    api.chatSettings(channel).then(setChatSettings).catch(() => {});
  }, [channel]);

  useEffect(() => {
    const socket = io(BASE, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join', {
        channel,
        username: user?.display_name || user?.username || 'Anonymous',
        token: localStorage.getItem('token') || '',
      });
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('message', (msg) => {
      setMessages(prev => [...prev.slice(-200), msg]);
    });

    socket.on('error', (data) => {
      setChatError(data.message || 'Error');
      setTimeout(() => setChatError(''), 4000);
    });

    socket.on('modaction', (data) => {
      if (data.type === 'ban' || data.type === 'timeout') {
        setMessages(prev => prev.map(m =>
          m.username === data.username ? { ...m, removed: true } : m
        ));
      }
    });

    // Listen for chat settings changes from streamer
    socket.on('chat_settings', (settings) => {
      setChatSettings(settings);
    });

    return () => {
      socket.emit('leave', { channel });
      socket.disconnect();
    };
  }, [channel]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  const sendMessage = (e) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || !socketRef.current || !user || cooldown > 0) return;
    socketRef.current.emit('message', {
      channel,
      message: msg,
      token: localStorage.getItem('token') || '',
    });
    setInput('');
    setShowEmotes(false);
    // Start client-side cooldown
    const slowMode = chatSettings?.slow_mode_seconds || 0;
    if (slowMode > 0 && !isStreamer) {
      setCooldown(slowMode);
    }
  };

  const sendCommand = (cmd) => {
    if (!socketRef.current || !user) return;
    socketRef.current.emit('message', {
      channel,
      message: cmd,
      token: localStorage.getItem('token') || '',
    });
  };

  const insertEmote = (code) => {
    setInput(prev => (prev ? prev + ' ' : '') + code + ' ');
  };

  // Chat mode indicators
  const modeLabels = [];
  if (chatSettings?.slow_mode_seconds > 0) modeLabels.push(`Slow ${chatSettings.slow_mode_seconds}s`);
  if (chatSettings?.sub_only) modeLabels.push('Sub only');
  else if (chatSettings?.follower_only) modeLabels.push('Followers');
  if (!chatSettings?.links_allowed) modeLabels.push('No links');

  return (
    <div className="chat-container">
      <div className="chat-header">
        <span className="panel-title" style={{ margin: 0 }}>Live Chat</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {modeLabels.map(label => (
            <span key={label} style={{
              fontSize: '0.6rem', fontWeight: 700, padding: '2px 5px',
              borderRadius: '3px', background: '#D6E9AA22', color: '#D6E9AA',
              textTransform: 'uppercase', letterSpacing: '0.3px',
            }}>{label}</span>
          ))}
          <span className={`chat-status ${connected ? 'online' : 'offline'}`}>
            {connected ? '● Live' : '○ Connecting...'}
          </span>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">Be the first to say something!</div>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className="chat-message"
            style={{ position: 'relative', opacity: msg.removed ? 0.3 : 1 }}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            {msg.is_system ? (
              <span style={{ color: '#adadb8', fontStyle: 'italic', fontSize: '0.82rem' }}>
                {msg.message}
              </span>
            ) : (
              <>
                {/* Badges */}
                {msg.badges?.length > 0 && (
                  <span className="chat-badges">
                    {msg.badges.map(b => {
                      const badge = BADGE_ICONS[b];
                      if (!badge) return null;
                      return (
                        <span key={b} className="chat-badge" title={badge.title}
                          style={{ background: badge.color + '22', color: badge.color }}>
                          {badge.label}
                        </span>
                      );
                    })}
                  </span>
                )}
                <span className={`chat-username ${msg.verified ? 'verified' : 'guest'}`}>
                  {msg.username}
                </span>
                <span className="chat-text">
                  {msg.removed ? '<message removed>' : renderMessage(msg.message)}
                </span>
              </>
            )}

            {/* Mod buttons */}
            {isStreamer && !msg.is_system && !msg.removed && hoveredIdx === i && msg.username !== user?.display_name && msg.username !== user?.username && (
              <span style={{ position: 'absolute', right: 0, top: 0, display: 'flex', gap: '4px', background: '#18181b', padding: '2px 4px', borderRadius: '4px' }}>
                <button
                  title="Ban"
                  onClick={() => sendCommand(`/ban ${msg.username}`)}
                  style={{ background: '#eb0400', border: 'none', color: '#fff', borderRadius: '4px', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700 }}
                >
                  Ban
                </button>
                <button
                  title="Timeout 5 min"
                  onClick={() => sendCommand(`/timeout ${msg.username} 5m`)}
                  style={{ background: '#2a2a2e', border: 'none', color: '#efeff1', borderRadius: '4px', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700 }}
                >
                  5m
                </button>
                <button
                  title="Timeout 1 hour"
                  onClick={() => sendCommand(`/timeout ${msg.username} 1h`)}
                  style={{ background: '#2a2a2e', border: 'none', color: '#efeff1', borderRadius: '4px', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700 }}
                >
                  1h
                </button>
              </span>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Emote picker */}
      {showEmotes && (
        <div className="emote-picker">
          <div className="emote-picker-header">
            <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>Emotes</span>
            <button onClick={() => setShowEmotes(false)}
              style={{ background: 'none', border: 'none', color: '#adadb8', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
          </div>
          <div className="emote-grid">
            {Object.entries(EMOTES).map(([code, emoji]) => (
              <button key={code} className="emote-btn" onClick={() => insertEmote(code)} title={code}>
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="chat-input-area">
        {chatError && (
          <p style={{ color: '#eb0400', fontSize: '0.8rem', marginBottom: '6px' }}>{chatError}</p>
        )}
        {!user && (
          <p style={{ fontSize: '0.82rem', color: '#adadb8', textAlign: 'center', padding: '4px 0' }}>
            Log in to chat
          </p>
        )}
        <form onSubmit={sendMessage} className="chat-form">
          <button
            type="button"
            className="emote-toggle"
            onClick={() => setShowEmotes(!showEmotes)}
            title="Emotes"
          >
            😀
          </button>
          <input
            className="input"
            placeholder={
              !user ? 'Log in to chat'
              : cooldown > 0 ? `Wait ${cooldown}s...`
              : connected ? (isStreamer ? '/ban, /timeout, /mod...' : 'Say something...')
              : 'Connecting...'
            }
            value={input}
            onChange={e => setInput(e.target.value)}
            disabled={!connected || !user || cooldown > 0}
            maxLength={300}
            style={{ marginBottom: 0, flex: 1 }}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!connected || !input.trim() || !user || cooldown > 0}
            style={{ padding: '8px 14px', flexShrink: 0 }}
          >
            {cooldown > 0 ? cooldown : 'Chat'}
          </button>
        </form>
      </div>
    </div>
  );
}
